/**
 * A tooltip positioned in script, so it shifts or flips to stay inside the
 * content column instead of painting under the sidebar or off screen.
 *
 * The trigger is a render prop: the caller decides which element takes the
 * handlers and whether `aria-describedby` points at the bubble, which it
 * should skip when the trigger already carries the same text.
 *
 * @module components/ui/FloatingTip
 */

import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";

import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import type { FocusEventHandler, MouseEventHandler, ReactNode } from "react";

export const FLOATING_TIP_PLACEMENTS = ["top", "bottom"] as const;
export type FloatingTipPlacement = (typeof FLOATING_TIP_PLACEMENTS)[number];

/** Spread onto the element that opens the tip. */
export interface FloatingTipTriggerProps {
  ref: (node: HTMLElement | null) => void;
  onMouseEnter: MouseEventHandler;
  onMouseLeave: MouseEventHandler;
  onFocus: FocusEventHandler;
  onBlur: FocusEventHandler;
  "aria-describedby": string | undefined;
}

export const FLOATING_TIP_TONES = ["dark", "soft"] as const;
export type FloatingTipTone = (typeof FLOATING_TIP_TONES)[number];

/** Written out so Tailwind's scanner sees each class. */
const TONE_CLASS: Record<FloatingTipTone, string> = {
  dark: "bg-neutral text-neutral-content",
  soft: "border border-border-strong bg-base-100 text-base-content",
};

export interface FloatingTipProps {
  tip: string;
  placement?: FloatingTipPlacement;
  /** `soft` is a light surface, for help text that should not outweigh the page. */
  tone?: FloatingTipTone;
  children: (trigger: FloatingTipTriggerProps) => ReactNode;
}

/** The column the tip stays inside; the viewport inside a dialog or without the shell. */
function contentBoundary(dialog: Element | null): Element | "clippingAncestors" {
  return (dialog ? null : document.getElementById("main-content")) ?? "clippingAncestors";
}

export function FloatingTip({
  tip,
  placement = "bottom",
  tone = "dark",
  children,
}: FloatingTipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState<HTMLElement | null>(null);
  const [floating, setFloating] = useState<HTMLElement | null>(null);
  // A modal dialog sits in the top layer, above anything in `body` whatever its
  // z-index, so a tip opened from inside one has to render inside it.
  const dialog = reference?.closest("dialog[open]") ?? null;
  const { floatingStyles } = useFloating<HTMLElement>({
    open,
    elements: { reference, floating },
    placement,
    strategy: "fixed",
    middleware: [
      offset(6),
      flip({ boundary: open ? contentBoundary(dialog) : undefined }),
      shift({ boundary: open ? contentBoundary(dialog) : undefined, padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      {children({
        ref: setReference,
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: () => setOpen(false),
        "aria-describedby": open ? id : undefined,
      })}
      {open &&
        createPortal(
          <span
            ref={setFloating}
            id={id}
            role="tooltip"
            style={floatingStyles}
            className={`pointer-events-none z-50 w-max max-w-xs rounded-sm px-2 py-1 text-sm shadow ${TONE_CLASS[tone]}`}
          >
            {tip}
          </span>,
          dialog ?? document.body,
        )}
    </>
  );
}
